import React, {useEffect, useMemo, useState} from "react";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const CATEGORY_LABELS = {
  DEVELOPMENT: "Rozwój",
  TRAINING: "Szkolenie",
  SKILLS: "Umiejętności",
  PERFORMANCE_REVIEW: "Rozmowa oceniająca"
};

const emptyReview = () => ({
  category: "PERFORMANCE_REVIEW",
  reviewDate: new Date().toISOString().slice(0, 10),
  score: "",
  weight: 1,
  reviewerId: "",
  description: ""
});

const EmployeeEvaluationCard = ({employeeId, supervisorsList = [], settings = {}}) => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles) ? auth.roles : String(auth?.roles || "").replace(/[\[\]\s]/g, "").split(",");
  const canRecalculate = roles.includes("ROLE_HR") || roles.includes("ROLE_ADMIN");
  const isManagerOnly = roles.includes("ROLE_MANAGER") && !canRecalculate;
  const allowed = canRecalculate || isManagerOnly;
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(emptyReview());
  const [saving, setSaving] = useState(false);

  const minScore = Number(settings?.positionEvaluationMinimumValue ?? 0);
  const maxScore = Number(settings?.positionEvaluationMaximumValue ?? 5);

  const reviewerOptions = useMemo(() => [...supervisorsList]
    .filter(item => item?.id)
    .sort((a, b) => `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`, "pl")), [supervisorsList]);

  const load = async () => {
    if (!employeeId || !allowed) return;
    try {
      const [summaryResponse, reviewsResponse] = await Promise.all([
        axiosPrivate.get(`/employee-evaluations/${employeeId}`),
        axiosPrivate.get(`/employee-performance-reviews/employee/${employeeId}`)
      ]);
      setSummary(summaryResponse.data);
      setReviews(reviewsResponse.data || []);
    } catch (e) {
      toastError(e?.response?.data?.message || e?.response?.data || "Nie udało się pobrać ocen pracownika");
    }
  };

  useEffect(() => {
    load();
  }, [employeeId, allowed]);

  const recalculate = async () => {
    try {
      const response = await axiosPrivate.post(`/employee-evaluations/${employeeId}/recalculate`);
      setSummary(response.data);
      toastSuccess("Przeliczono ocenę pracownika");
    } catch (e) {
      toastError(e?.response?.data?.message || e?.response?.data || "Nie udało się przeliczyć oceny pracownika");
    }
  };

  const addReview = async () => {
    const score = Number(form.score);
    const weight = Number(form.weight);
    if (!Number.isFinite(score) || score < minScore || score > maxScore) {
      toastError(`Ocena musi mieścić się w zakresie ${minScore}-${maxScore}`);
      return;
    }
    if (!Number.isFinite(weight) || weight < 0) {
      toastError("Waga nie może być ujemna");
      return;
    }
    if (canRecalculate && !form.reviewerId) {
      toastError("Wskaż osobę wystawiającą ocenę");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/employee-performance-reviews", {
        employeeId,
        category: form.category,
        reviewDate: form.reviewDate,
        score,
        weight,
        reviewerId: form.reviewerId ? Number(form.reviewerId) : null,
        description: form.description
      });
      setForm(emptyReview());
      await load();
      toastSuccess("Dodano ocenę pracownika");
    } catch (e) {
      toastError(e?.response?.data?.message || e?.response?.data || "Nie udało się dodać oceny pracownika");
    } finally {
      setSaving(false);
    }
  };

  const deleteReview = async id => {
    try {
      await axiosPrivate.delete(`/employee-performance-reviews/${id}`);
      await load();
      toastSuccess("Usunięto ocenę pracownika");
    } catch (e) {
      toastError(e?.response?.data?.message || e?.response?.data || "Nie udało się usunąć oceny pracownika");
    }
  };

  if (!allowed || !employeeId) return null;

  const score = value => value == null ? "—" : Number(value).toFixed(2);
  const percent = value => value == null ? null : `${Number(value).toFixed(2)}%`;
  const reviewerName = review => [review.reviewerFirstName, review.reviewerLastName].filter(Boolean).join(" ") || `ID ${review.reviewerId}`;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Oceny pracownika</strong>
        {canRecalculate && <button type="button" className="btn btn-sm btn-outline-primary" onClick={recalculate}>Przelicz teraz</button>}
      </div>
      <div className="card-body">
        <div className="row g-2 text-center mb-3">
          <div className="col-md"><div className="border rounded p-2"><small>Ocena ogólna</small><div className="fs-4 fw-bold">{score(summary?.evaluation)}</div></div></div>
          <div className="col-md"><div className="border rounded p-2"><small>Umiejętności × {summary?.softSkillsWeight ?? 1}</small><div className="fw-bold">{score(summary?.softSkillsScore)}</div></div></div>
          <div className="col-md"><div className="border rounded p-2"><small>Stanowiska × {summary?.positionsWeight ?? 1}</small><div className="fw-bold">{score(summary?.positionsScore)}</div></div></div>
          <div className="col-md"><div className="border rounded p-2"><small>Szkolenia × {summary?.trainingsWeight ?? 1}</small><div className="fw-bold">{score(summary?.trainingsScore)}</div></div></div>
          <div className="col-md">
            <div className="border rounded p-2">
              <small>Testy × {summary?.trainingTestsWeight ?? 1}</small>
              <div className="fw-bold">{score(summary?.trainingTestsScore)}</div>
              {percent(summary?.trainingTestsAveragePercent) && <div className="small text-muted">średnia {percent(summary.trainingTestsAveragePercent)}</div>}
            </div>
          </div>
          <div className="col-md"><div className="border rounded p-2"><small>Rozwój i rozmowy × {summary?.reviewsWeight ?? 1}</small><div className="fw-bold">{score(summary?.reviewsScore)}</div></div></div>
          <div className="col-md"><div className="border rounded p-2"><small>Zdarzenia × {summary?.eventsWeight ?? 1}</small><div className="fw-bold">{score(summary?.eventsScore)}</div></div></div>
        </div>

        <div className="border rounded p-3 mb-3">
          <div className="fw-semibold mb-2">Nowa ocena punktowa i opisowa</div>
          <div className="row g-2 align-items-end">
            <div className="col-lg-2 col-md-4">
              <label className="form-label small">Kategoria</label>
              <select className="form-select" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="form-label small">Data</label>
              <input type="date" className="form-control" value={form.reviewDate} onChange={e => setForm({...form, reviewDate: e.target.value})}/>
            </div>
            <div className="col-lg-1 col-md-2">
              <label className="form-label small">Punkty</label>
              <input type="number" min={minScore} max={maxScore} step="0.01" className="form-control" value={form.score} onChange={e => setForm({...form, score: e.target.value})}/>
            </div>
            <div className="col-lg-1 col-md-2">
              <label className="form-label small">Waga</label>
              <input type="number" min="0" step="0.1" className="form-control" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})}/>
            </div>
            {canRecalculate && (
              <div className="col-lg-2 col-md-4">
                <label className="form-label small">Wystawił(a)</label>
                <select className="form-select" value={form.reviewerId} onChange={e => setForm({...form, reviewerId: e.target.value})}>
                  <option value="">-- wybierz --</option>
                  {reviewerOptions.map(item => <option key={item.id} value={item.id}>{item.firstName} {item.lastName}</option>)}
                </select>
              </div>
            )}
            <div className={canRecalculate ? "col-lg-3 col-md-6" : "col-lg-5 col-md-8"}>
              <label className="form-label small">Ocena opisowa</label>
              <input className="form-control" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Wnioski z rozmowy, rozwój, mocne strony, obszary do poprawy"/>
            </div>
            <div className="col-lg-1 col-md-2">
              <button type="button" className="btn btn-primary w-100" onClick={addReview} disabled={saving}>{saving ? "..." : "Dodaj"}</button>
            </div>
          </div>
          {isManagerOnly && <div className="small text-muted mt-2">Osoba wystawiająca jest ustawiana automatycznie jako zalogowany kierownik.</div>}
        </div>

        <div className="table-responsive mb-2">
          <table className="table table-sm align-middle mb-0">
            <thead><tr><th>Data</th><th>Kategoria</th><th>Punkty</th><th>Waga</th><th>Wystawił(a)</th><th>Ocena opisowa</th><th></th></tr></thead>
            <tbody>
              {reviews.map(review => (
                <tr key={review.id}>
                  <td>{review.reviewDate}</td>
                  <td>{CATEGORY_LABELS[review.category] || review.category}</td>
                  <td>{score(review.score)}</td>
                  <td>{Number(review.weight ?? 1).toFixed(2)}</td>
                  <td>{reviewerName(review)}</td>
                  <td>{review.description || "—"}</td>
                  <td className="text-end"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteReview(review.id)}>Usuń</button></td>
                </tr>
              ))}
              {!reviews.length && <tr><td colSpan="7" className="text-muted">Brak ocen opisowych i rozmów oceniających.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="small text-muted mt-2">
          Ocena ogólna łączy umiejętności, stanowiska, szkolenia, testy, rozmowy i oceny rozwojowe oraz zdarzenia krytyczne. Wagi grup ustawia się w Ustawieniach, a waga pojedynczej oceny pozwala określić jej znaczenie w grupie „Rozwój i rozmowy”.
        </div>
      </div>
    </div>
  );
};

export default EmployeeEvaluationCard;
